import React from 'react';

import { Button, Group, Radio, Stack, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import KiloAccordion from 'components/KiloAccordion';
import { useLocalization } from 'context/localization-context';
import useEligibleDiscounts from 'hooks/discount/useEligibleDiscounts';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { EligibleDiscountList } from 'types';
import { formatDiscountValue, getDiscountFormValue } from 'utils/discount.helpers';

interface AvailableDiscountsProps {
  athleteId: string | undefined;
  productId: string | undefined;
  selectedDiscountId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturnType<any>;
  fieldPath: string;
  oneLine?: boolean;
}

const AvailableDiscounts = ({
  athleteId,
  productId,
  selectedDiscountId,
  form,
  fieldPath,
  oneLine,
}: AvailableDiscountsProps): JSX.Element | null => {
  const { formatCurrency } = useLocalization();
  const { trackDiscountApplied } = useStaffTracking();
  const { data } = useEligibleDiscounts({ productId, athleteId });

  const discounts = data?.data ?? [];

  if (discounts.length === 0) return null;

  const basePath = fieldPath.replace(/\.discount$/, '');

  const clearSelection = (): void => {
    form.setFieldValue(`${basePath}.discount`, null);
    form.setFieldValue(`${basePath}.discountValue`, undefined);
    form.setFieldValue(`${basePath}.discountType`, undefined);
    form.setFieldValue(`${basePath}.discountInfo`, undefined);
    form.setFieldValue(`${basePath}.discountName`, null);
  };

  const handleSelect = (discount: EligibleDiscountList): void => {
    if (discount.isRedeemedByAthlete) return;
    const isDeselect = selectedDiscountId === discount.discountId;
    if (isDeselect) {
      clearSelection();
      return;
    }
    form.setFieldValue(`${basePath}.discount`, discount.discountId);
    form.setFieldValue(`${basePath}.discountValue`, getDiscountFormValue(discount));
    form.setFieldValue(`${basePath}.discountType`, discount.valueType);
    form.setFieldValue(`${basePath}.discountInfo`, discount);
    form.setFieldValue(`${basePath}.discountName`, discount.name);
    trackDiscountApplied(discount.discountId, discount.name, discount.valueType, 'invoice_item');
  };

  return (
    <KiloAccordion
      w={'fit-content'}
      title='Available Discounts'
      onTitleClick={() => {}}
      rootVariantType='shoppingCart'
      titleRootVariantType={'flat'}
    >
      <Radio.Group value={selectedDiscountId ?? ''} onChange={() => {}}>
        <Stack spacing='md'>
          {discounts.map(d => {
            const isRedeemed = d.isRedeemedByAthlete;
            return (
              <Stack key={d.discountId} spacing='xs' style={oneLine ? { flexDirection: 'row' } : {}}>
                <Radio
                  value={d.discountId}
                  label={d.name}
                  size='xs'
                  disabled={isRedeemed}
                  styles={{ label: { fontSize: '12px', paddingLeft: '5px' } }}
                  onClick={() => handleSelect(d)}
                />
                <Text
                  ml={'19px'}
                  size='xs'
                  color={d.discountId === selectedDiscountId && !isRedeemed ? 'green' : 'gray'}
                >
                  {formatDiscountValue(d, formatCurrency)}
                </Text>
              </Stack>
            );
          })}
          {selectedDiscountId && (
            <Group position='left'>
              <Button size='xs' variant='outline' color='red' onClick={clearSelection}>
                Remove Selection
              </Button>
            </Group>
          )}
        </Stack>
      </Radio.Group>
    </KiloAccordion>
  );
};

export default AvailableDiscounts;
